import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend as RLegend,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
} from "recharts";
import { useTranslation, useFormat } from "../../i18n";
import { AXIS_TICK, AXIS_TICK_SMALL, INK, SERIES, SERIES_ROLE, TOOLTIP_STYLE, sequential, seriesColor } from "./palette";
import { EmptyState, Legend } from "./primitives";
import { useResponsive } from "../responsive";

/* ------------------------------------------------------------------------ */
/* Bucket labels                                                              */
/* ------------------------------------------------------------------------ */

export const useBucketLabel = (granularity) => {
  const fmt = useFormat();
  const { t } = useTranslation("analytics");
  return useMemo(
    () => (bucket) => {
      if (!bucket) return "";
      const d = new Date(`${bucket}T00:00:00`);
      if (granularity === "month") return fmt.date(d, { month: "short", year: "2-digit" });
      if (granularity === "week") return t("week_of", { date: fmt.date(d, { month: "short", day: "numeric" }) });
      return fmt.date(d, { month: "short", day: "numeric" });
    },
    [granularity, fmt, t]
  );
};

const useTooltipFormatter = () => {
  const fmt = useFormat();
  return (value, name, item) => {
    const unit = item?.payload?.__unit || item?.unit;
    if (typeof value !== "number") return [value, name];
    if (unit === "currency") return [fmt.currency(value, "USD"), name];
    if (unit === "percent") return [fmt.number(value / 100, { style: "percent", maximumFractionDigits: 1 }), name];
    return [fmt.number(value, { maximumFractionDigits: 1 }), name];
  };
};

/* ------------------------------------------------------------------------ */
/* Trend: one metric over time, optional previous-period overlay              */
/* ------------------------------------------------------------------------ */

/**
 * rows: [{bucket, ...}], previous: aligned rows for the prior period.
 * series: [{key, label, color?, kind: "bar"|"line"|"area"}]
 * One y-axis only: every series must share a unit.
 */
export const TrendChart = ({ rows, previous, series, granularity, height = 260, unit, previousLabel, stacked = false }) => {
  const label = useBucketLabel(granularity);
  const formatter = useTooltipFormatter();
  const { isMobile } = useResponsive();
  const { t } = useTranslation("analytics");
  const data = useMemo(() => {
    if (!rows) return [];
    return rows.map((r, i) => {
      const row = { ...r, label: label(r.bucket), __unit: unit };
      if (previous && previous[i]) {
        series.forEach((s) => {
          row[`prev_${s.key}`] = previous[i][s.key];
        });
      }
      return row;
    });
  }, [rows, previous, series, label, unit]);
  if (!data.length || !data.some((d) => series.some((s) => d[s.key]))) return <EmptyState height={height} />;

  const withPrev = previous && previous.length > 0;
  const legendItems = [
    ...series.map((s, i) => ({ label: s.label, color: s.color || seriesColor(i) })),
    ...(withPrev ? [{ label: previousLabel || t("previous_period"), color: SERIES_ROLE.previous, dashed: true }] : []),
  ];

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={2} barCategoryGap="25%">
          <CartesianGrid vertical={false} stroke={INK.grid} strokeDasharray="2 4" />
          <XAxis dataKey="label" tick={isMobile ? AXIS_TICK_SMALL : AXIS_TICK} tickLine={false} axisLine={{ stroke: INK.axis }} interval="preserveStartEnd" minTickGap={24} />
          <YAxis tick={isMobile ? AXIS_TICK_SMALL : AXIS_TICK} tickLine={false} axisLine={false} width={isMobile ? 30 : 42} allowDecimals={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "rgba(124,58,237,0.06)" }} formatter={formatter} />
          {series.map((s, i) => {
            const color = s.color || seriesColor(i);
            if (s.kind === "line") return <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />;
            if (s.kind === "area")
              return <Area key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={color} strokeWidth={2} fill={color} fillOpacity={0.12} dot={false} activeDot={{ r: 4 }} />;
            return <Bar key={s.key} dataKey={s.key} name={s.label} fill={color} radius={stacked ? 0 : [4, 4, 0, 0]} stackId={stacked ? "s" : undefined} stroke="#fff" strokeWidth={stacked ? 1 : 0} maxBarSize={36} />;
          })}
          {withPrev &&
            series.slice(0, 1).map((s) => (
              <Line key={`prev_${s.key}`} type="monotone" dataKey={`prev_${s.key}`} name={`${previousLabel || t("previous_period")}`} stroke={SERIES_ROLE.previous} strokeWidth={2} strokeDasharray="4 4" dot={false} activeDot={{ r: 3 }} />
            ))}
        </ComposedChart>
      </ResponsiveContainer>
      {legendItems.length > 1 && <Legend items={legendItems} />}
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* Stacked series keyed by dynamic columns (by type over time)               */
/* ------------------------------------------------------------------------ */

export const StackedTrendChart = ({ rows, keys, granularity, height = 260, maxSeries = 7 }) => {
  const label = useBucketLabel(granularity);
  const formatter = useTooltipFormatter();
  const { isMobile } = useResponsive();
  const { t } = useTranslation("analytics");
  const { data, shown } = useMemo(() => {
    const kept = (keys || []).slice(0, maxSeries);
    const rest = (keys || []).slice(maxSeries);
    const mapped = (rows || []).map((r) => {
      const row = { label: label(r.bucket) };
      kept.forEach((k) => {
        row[k.key] = r[k.key] || 0;
      });
      if (rest.length) row.__other = rest.reduce((s, k) => s + (r[k.key] || 0), 0);
      return row;
    });
    return { data: mapped, shown: rest.length ? [...kept, { key: "__other", name: t("other"), color: SERIES_ROLE.other }] : kept };
  }, [rows, keys, label, maxSeries, t]);
  if (!data.length || !shown.length) return <EmptyState height={height} />;
  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="25%">
          <CartesianGrid vertical={false} stroke={INK.grid} strokeDasharray="2 4" />
          <XAxis dataKey="label" tick={isMobile ? AXIS_TICK_SMALL : AXIS_TICK} tickLine={false} axisLine={{ stroke: INK.axis }} interval="preserveStartEnd" minTickGap={24} />
          <YAxis tick={isMobile ? AXIS_TICK_SMALL : AXIS_TICK} tickLine={false} axisLine={false} width={isMobile ? 30 : 42} allowDecimals={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "rgba(124,58,237,0.06)" }} formatter={formatter} />
          {shown.map((k, i) => (
            <Bar key={k.key} dataKey={k.key} name={k.name} stackId="a" fill={k.color || seriesColor(i)} stroke="#fff" strokeWidth={1} maxBarSize={36} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <Legend items={shown.map((k, i) => ({ label: k.name, color: k.color || seriesColor(i) }))} />
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* Donut                                                                      */
/* ------------------------------------------------------------------------ */

export const DonutChart = ({ data, nameKey = "name", valueKey = "count", height = 240, maxSlices = 7, centerLabel }) => {
  const fmt = useFormat();
  const { t } = useTranslation("analytics");
  const { isMobile } = useResponsive();
  const slices = useMemo(() => {
    const rows = (data || []).filter((d) => d[valueKey] > 0);
    const kept = rows.slice(0, maxSlices).map((d, i) => ({ name: d[nameKey], value: d[valueKey], color: d.color || seriesColor(i) }));
    const rest = rows.slice(maxSlices);
    if (rest.length) kept.push({ name: t("other"), value: rest.reduce((s, d) => s + d[valueKey], 0), color: SERIES_ROLE.other });
    return kept;
  }, [data, nameKey, valueKey, maxSlices, t]);
  if (!slices.length) return <EmptyState height={height} />;
  const total = slices.reduce((s, d) => s + d.value, 0);
  return (
    <div className="flex flex-col sm:flex-row items-center gap-3">
      <div className="relative w-full sm:w-1/2" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={slices} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={isMobile ? 50 : 62} outerRadius={isMobile ? 78 : 96} paddingAngle={2} stroke="#fff" strokeWidth={2}>
              {slices.map((s) => (
                <Cell key={s.name} fill={s.color} />
              ))}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, n) => [`${fmt.number(v)} (${fmt.number(v / total, { style: "percent", maximumFractionDigits: 0 })})`, n]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-xl font-bold text-slate-900">{fmt.number(total)}</span>
          {centerLabel && <span className="text-[10px] text-slate-500">{centerLabel}</span>}
        </div>
      </div>
      <ul className="w-full sm:w-1/2 space-y-1.5 text-xs">
        {slices.map((s) => (
          <li key={s.name} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: s.color }} aria-hidden="true" />
            <span className="flex-1 truncate text-slate-700">{s.name}</span>
            <span className="tabular-nums text-slate-500">{fmt.number(s.value / total, { style: "percent", maximumFractionDigits: 0 })}</span>
            <span className="tabular-nums font-medium text-slate-800 w-10 text-right">{fmt.number(s.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* Horizontal bar list: ranked categories, direct labels, no axis needed     */
/* ------------------------------------------------------------------------ */

export const BarList = ({ data, nameKey = "name", valueKey = "count", shareKey, color, colorKey, max, formatValue, emptyMessage, renderName }) => {
  const fmt = useFormat();
  if (!data || !data.length) return <EmptyState message={emptyMessage} height={140} />;
  const top = max || Math.max(...data.map((d) => d[valueKey] || 0), 1);
  return (
    <ul className="space-y-2">
      {data.map((d, i) => (
        <li key={d[nameKey] || i}>
          <div className="flex items-center justify-between gap-2 text-xs mb-0.5">
            <span className="truncate text-slate-700 min-w-0">{renderName ? renderName(d) : d[nameKey]}</span>
            <span className="tabular-nums text-slate-800 font-medium flex-shrink-0">
              {formatValue ? formatValue(d) : fmt.number(d[valueKey])}
              {shareKey && d[shareKey] !== null && d[shareKey] !== undefined && (
                <span className="text-slate-400 font-normal ml-1">{fmt.number(d[shareKey] / 100, { style: "percent", maximumFractionDigits: 0 })}</span>
              )}
            </span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
            <div className="h-full rounded-full" style={{ width: `${Math.max(2, ((d[valueKey] || 0) / top) * 100)}%`, background: (colorKey && d[colorKey]) || color || SERIES[0] }} />
          </div>
        </li>
      ))}
    </ul>
  );
};

/* ------------------------------------------------------------------------ */
/* Histogram (discrete buckets)                                               */
/* ------------------------------------------------------------------------ */

export const Histogram = ({ data, labelKey = "bucket", valueKey = "users", height = 200, color = SERIES[0], formatLabel }) => {
  const fmt = useFormat();
  const { isMobile } = useResponsive();
  if (!data || !data.some((d) => d[valueKey] > 0)) return <EmptyState height={height} />;
  const rows = data.map((d) => ({ ...d, label: formatLabel ? formatLabel(d) : d[labelKey] }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} margin={{ top: 16, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
        <CartesianGrid vertical={false} stroke={INK.grid} strokeDasharray="2 4" />
        <XAxis dataKey="label" tick={isMobile ? AXIS_TICK_SMALL : AXIS_TICK} tickLine={false} axisLine={{ stroke: INK.axis }} interval={0} />
        <YAxis tick={isMobile ? AXIS_TICK_SMALL : AXIS_TICK} tickLine={false} axisLine={false} width={isMobile ? 28 : 40} allowDecimals={false} />
        <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "rgba(124,58,237,0.06)" }} formatter={(v) => [fmt.number(v), ""]} />
        <Bar dataKey={valueKey} fill={color} radius={[4, 4, 0, 0]} maxBarSize={48} label={{ position: "top", fontSize: 10, fill: INK.secondary, formatter: (v) => (v ? fmt.number(v) : "") }} />
      </BarChart>
    </ResponsiveContainer>
  );
};

/* ------------------------------------------------------------------------ */
/* Heatmap: weekday x hour                                                    */
/* ------------------------------------------------------------------------ */

export const WeekHourHeatmap = ({ heatmap, weekStartDay = 0 }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  if (!heatmap || !heatmap.cells || heatmap.max === 0) return <EmptyState height={200} />;
  const lookup = {};
  heatmap.cells.forEach((c) => {
    lookup[`${c.weekday}-${c.hour}`] = c.count;
  });
  const days = Array.from({ length: 7 }, (_, i) => (weekStartDay + i) % 7);
  const dayNames = days.map((d) => {
    const ref = new Date(2024, 0, 1 + d); // 2024-01-01 is a Monday
    return fmt.date(ref, { weekday: "short" });
  });
  const hours = Array.from({ length: 24 }, (_, i) => i);
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[560px]">
        <div className="grid" style={{ gridTemplateColumns: "44px repeat(24, minmax(0, 1fr))", gap: 2 }}>
          <div />
          {hours.map((h) => (
            <div key={h} className="text-[9px] text-slate-400 text-center">
              {h % 3 === 0 ? h : ""}
            </div>
          ))}
          {days.map((d, di) => (
            <React.Fragment key={d}>
              <div className="text-[10px] text-slate-500 pr-1 flex items-center">{dayNames[di]}</div>
              {hours.map((h) => {
                const v = lookup[`${d}-${h}`] || 0;
                return (
                  <div
                    key={`${d}-${h}`}
                    className="h-5 rounded-[3px]"
                    style={{ background: sequential(v / heatmap.max) }}
                    title={`${dayNames[di]} ${h}:00 · ${t("recognitions_count", { count: v })}`}
                    role="img"
                    aria-label={`${dayNames[di]} ${h}:00, ${v}`}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <div className="flex items-center justify-end gap-1 mt-2 text-[10px] text-slate-400">
          <span>{t("heatmap.less")}</span>
          {[0, 0.2, 0.4, 0.6, 0.8, 1].map((r) => (
            <span key={r} className="w-4 h-3 rounded-[2px]" style={{ background: sequential(r) }} aria-hidden="true" />
          ))}
          <span>{t("heatmap.more")}</span>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* Flow matrix: group -> group counts                                         */
/* ------------------------------------------------------------------------ */

export const FlowMatrix = ({ matrix, emptyMessage }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  if (!matrix || !matrix.nodes || matrix.nodes.length === 0 || !matrix.cells?.length) return <EmptyState message={emptyMessage} height={200} />;
  const nodes = matrix.nodes;
  const lookup = {};
  let max = 0;
  matrix.cells.forEach((c) => {
    lookup[`${c.from}|${c.to}`] = c.count;
    if (c.count > max) max = c.count;
  });
  return (
    <div className="overflow-x-auto">
      <table className="text-[11px] border-separate" style={{ borderSpacing: 2 }}>
        <thead>
          <tr>
            <th className="text-left text-slate-400 font-normal pr-2 align-bottom">{t("matrix.from_to")}</th>
            {nodes.map((n) => (
              <th key={n.key} className="text-slate-600 font-medium max-w-[72px] truncate align-bottom px-1" title={n.name}>
                <div className="truncate" style={{ maxWidth: 72 }}>{n.name}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {nodes.map((from) => (
            <tr key={from.key}>
              <th scope="row" className="text-left text-slate-600 font-medium pr-2 max-w-[110px] truncate" title={from.name}>
                <div className="truncate" style={{ maxWidth: 110 }}>{from.name}</div>
              </th>
              {nodes.map((to) => {
                const v = lookup[`${from.key}|${to.key}`] || 0;
                const dark = v / max > 0.55;
                return (
                  <td
                    key={to.key}
                    className={`text-center rounded-[4px] w-11 h-8 tabular-nums ${from.key === to.key ? "ring-1 ring-inset ring-purple-200" : ""}`}
                    style={{ background: sequential(v / max), color: dark ? "#fff" : INK.primary }}
                    title={`${from.name} → ${to.name}: ${fmt.number(v)}`}
                  >
                    {v || ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* Radar: a person's recognition profile                                     */
/* ------------------------------------------------------------------------ */

export const ProfileRadar = ({ data, height = 240 }) => {
  const fmt = useFormat();
  if (!data || data.length < 3) return null;
  const rows = data.slice(0, 8).map((d) => ({ subject: `${d.emoji && !String(d.emoji).startsWith("/") ? `${d.emoji} ` : ""}${d.name}`, value: d.count }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <RadarChart data={rows} outerRadius="70%">
        <PolarGrid stroke={INK.grid} />
        <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: INK.secondary }} />
        <Radar dataKey="value" stroke={SERIES[0]} fill={SERIES[0]} fillOpacity={0.25} strokeWidth={2} />
        <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [fmt.number(v), ""]} />
      </RadarChart>
    </ResponsiveContainer>
  );
};

/* ------------------------------------------------------------------------ */
/* Two-segment comparison bar (e.g. intra vs cross)                           */
/* ------------------------------------------------------------------------ */

export const SegmentBar = ({ segments, height = 14 }) => {
  const fmt = useFormat();
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  if (!total) return <EmptyState height={60} />;
  return (
    <div>
      <div className="flex w-full rounded-full overflow-hidden bg-slate-100" style={{ height }} role="img" aria-label={segments.map((s) => `${s.label} ${s.value}`).join(", ")}>
        {segments.map((s, i) => (
          <div key={s.label} style={{ width: `${(s.value / total) * 100}%`, background: s.color || seriesColor(i), marginLeft: i ? 2 : 0 }} title={`${s.label}: ${fmt.number(s.value)}`} />
        ))}
      </div>
      <Legend items={segments.map((s, i) => ({ label: `${s.label} · ${fmt.number(s.value / total, { style: "percent", maximumFractionDigits: 0 })}`, color: s.color || seriesColor(i) }))} />
    </div>
  );
};

export { RLegend };
